import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { htmlToMarkdown } from './htmlToMarkdown';
import iconChatGPT from '../../assets/img/ai-tools/chatgpt.svg';
import iconClaude from '../../assets/img/ai-tools/claude.svg';
import iconGrok from '../../assets/img/ai-tools/grok.svg';

// COLUMNA DERECHA DE LA DOCUMENTACIÓN
//   - "Is this helpful?"      -> placeholder del feedback (comentado de momento)
//   - "Connect your AI agent" -> placeholder (comentado de momento)
//   - "Copy as Markdown"      -> copia la sección que se está leyendo, en Markdown
//   - "Ask ChatGPT / Claude / Grok" -> abre el chat con el enlace a la sección
//   - "On this page"          -> solo el título (comentado de momento)
// Estilos en assets/scss/pages/_docsCursor.scss (sección 6)
// Iconos: Lucide (licencia ISC) y logos de ChatGPT / Claude / Grok (assets/img/ai-tools).

const iconProps = {
  xmlns: 'http://www.w3.org/2000/svg',
  width: 14,
  height: 14,
  viewBox: '0 0 24 24',
  'aria-hidden': true,
};

// Para "Is this helpful?"
// const IconX = () => (
//   <svg {...iconProps} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
//     <path d="M18 6 6 18" />
//     <path d="m6 6 12 12" />
//   </svg>
// );

const IconCheck = () => (
  <svg {...iconProps} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// Para "Connect your AI agent"
// const IconSparkles = () => (
//   <svg {...iconProps} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
//     <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
//     <path d="M20 3v4" />
//     <path d="M22 5h-4" />
//     <path d="M4 17v2" />
//     <path d="M5 18H3" />
//   </svg>
// );

const IconCopy = () => (
  <svg {...iconProps} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
  </svg>
);

// Logos de las IA: SVG oficiales guardados en assets/img/ai-tools (LobeHub Icons).
// Se pintan como máscara con el color del texto, para que cambien de color al
// pasar el ratón igual que el resto de herramientas
const BrandIcon = ({ src }: { src: string }) => (
  <span className="docs-right-sidebar__brand-icon" style={{ WebkitMaskImage: `url(${src})`, maskImage: `url(${src})` }} aria-hidden="true" />
);

// La página de docs es una sola página larga con todas las secciones; "la página"
// para estas herramientas es la sección que se está leyendo: la última cuyo
// principio ya ha pasado bajo la cabecera (o la primera, si aún no ha pasado ninguna)
const getCurrentSection = (): Element | null => {
  const sections = Array.from(document.querySelectorAll(".contentMarginX > [class*='col-'] > [id]"))
    .filter((s) => getComputedStyle(s).display !== 'none');
  // Páginas sin secciones (la portada /default): se usa la columna entera
  if (!sections.length) return document.querySelector('.contentMarginX');

  const header = document.getElementById('devNav');
  const subbar = document.querySelector('.docs-subbar');
  const subbarBottom = subbar && getComputedStyle(subbar).display !== 'none' ? subbar.getBoundingClientRect().bottom : 0;
  const offset = Math.max(header ? header.getBoundingClientRect().bottom : 0, subbarBottom) + 24;

  let current = sections[0];
  sections.forEach((s) => {
    if (s.getBoundingClientRect().top <= offset + 1) current = s;
  });
  return current;
};

const sectionUrl = (section: Element | null): string => {
  const base = `${window.location.origin}${window.location.pathname}`;
  return section && section.id ? `${base}#${encodeURIComponent(section.id)}` : base;
};

// Mensaje que se envía al chat con el enlace a la sección
const askPrompt = () => `Read from ${sectionUrl(getCurrentSection())} so I can ask questions about its contents`;
const askChatGPTUrl = () => `https://chatgpt.com/?hint=search&q=${encodeURIComponent(askPrompt())}`;
const askClaudeUrl = () => `https://claude.ai/new?q=${encodeURIComponent(askPrompt())}`;
const askGrokUrl = () => `https://grok.com/?q=${encodeURIComponent(askPrompt())}`;

const copyText = async (text: string): Promise<void> => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Respaldo para contextos sin la API del portapapeles (http que no sea localhost)
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(area);
  if (!ok) throw new Error('copy failed');
};

// El href de los enlaces se calcula al usarlos, porque depende de la sección
// que se esté leyendo en ese momento
const setHref = (getUrl: () => string) => (e: React.SyntheticEvent<HTMLAnchorElement>) => {
  e.currentTarget.href = getUrl();
};

const DocsRightSidebar: FunctionComponent = () => {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const resetTimer = useRef<number>();

  useEffect(() => () => window.clearTimeout(resetTimer.current), []);

  const handleCopy = async () => {
    const section = getCurrentSection();
    try {
      if (!section) throw new Error('no section');
      await copyText(htmlToMarkdown(section));
      setCopyState('copied');
    } catch (e) {
      console.error('Copy as Markdown:', e);
      setCopyState('error');
    }
    window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(() => setCopyState('idle'), 2000);
  };

  return (
    <aside className="docs-right-sidebar" aria-label="Page tools">
      <div className="docs-right-sidebar__inner">
        {/* Sin funcionalidad todavía
        <section className="docs-right-sidebar__section">
          <h2 className="docs-right-sidebar__title">Is this helpful?</h2>
          <div className="docs-right-sidebar__feedback">
            <button type="button" className="docs-right-sidebar__feedback-btn" aria-label="No">
              <IconX />
            </button>
            <button type="button" className="docs-right-sidebar__feedback-btn" aria-label="Yes">
              <IconCheck />
            </button>
          </div>
        </section>
        */}

        <section className="docs-right-sidebar__section">
          <h3 className="docs-right-sidebar__title docs-right-sidebar__title--upper">AI Tools</h3>
          <div className="docs-right-sidebar__tools">
            {/* Placeholder: pendiente de definir
            <button type="button" className="docs-right-sidebar__tool">
              <IconSparkles />
              <span>Connect your AI agent</span>
            </button>
            */}

            <button type="button" className="docs-right-sidebar__tool" onClick={handleCopy} aria-live="polite">
              {copyState === 'copied' ? <IconCheck /> : <IconCopy />}
              <span>{copyState === 'copied' ? 'Copied!' : copyState === 'error' ? 'Copy failed' : 'Copy as Markdown'}</span>
            </button>

            {[
              { label: 'Ask ChatGPT', icon: iconChatGPT, getUrl: askChatGPTUrl },
              { label: 'Ask Claude', icon: iconClaude, getUrl: askClaudeUrl },
              { label: 'Ask Grok', icon: iconGrok, getUrl: askGrokUrl },
            ].map(({ label, icon, getUrl }) => (
              <a
                key={label}
                className="docs-right-sidebar__tool"
                href={sectionUrl(null)}
                target="_blank"
                rel="noreferrer noopener"
                onMouseDown={setHref(getUrl)}
                onFocus={setHref(getUrl)}
                onClick={setHref(getUrl)}
              >
                <BrandIcon src={icon} />
                <span>{label}</span>
              </a>
            ))}
          </div>
        </section>

        {/* Pendiente del índice de la página
        <section className="docs-right-sidebar__section">
          <h3 className="docs-right-sidebar__title docs-right-sidebar__title--upper docs-right-sidebar__title--strong">On this page</h3>
        </section>
        */}
      </div>
    </aside>
  );
};

export default DocsRightSidebar;
